import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { DetectionRuleState, NewDetectionRule, EditDetectionRule } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import ScreenUrl from '../../../router/url';
import router from '../../../router';

export const actions: ActionTree<DetectionRuleState, RootState> = {
    /**
     * 検知ルール一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} ignoreGroupId 除外するグループID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { word = '', ignoreGroupId = undefined, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: word,
            ignore_group_id: ignoreGroupId,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.DetectionRule.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 検知ルール新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearError({ commit }) {
        commit(types.CLEAR_ERROR_RULE);
    },

    /**
     * 検知ルール新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewDetectionRule} newDetectionRule 新規検知ルール情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newDetectionRule: NewDetectionRule) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.DetectionRule.CREATE, params: newDetectionRule },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.DetectionRule.CREATED);
                dispatch('toastMessage', `${newDetectionRule.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * 検知ルールの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 検知ルールID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.DetectionRule.SHOW.replace(':id', id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 検知ルール更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditDetectionRule} editDetectionRule 更新する検知ルール情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, editDetectionRule: EditDetectionRule) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.DetectionRule.UPDATE.replace(':id', editDetectionRule.id.toString()),
                    params: editDetectionRule,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.DetectionRule.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * 検知ルール削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id 検知ルールID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.DetectionRule.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.DetectionRule.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} ignoreGroupId 除外するグループID
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { word = '', ignoreGroupId = undefined, sort } = {}) {
        const payload = {
            word: word,
            ignore_group_id: ignoreGroupId,
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.DetectionRule.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
